import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "quickstart-for-pdf-accessibility-auto-tag-api-net"
    }}>{`Quickstart for PDF Accessibility Auto-Tag API (.NET)`}</h1>
    <p>{`To get started using Adobe PDF Accessibility Auto-Tag API, let's walk through a simple scenario - taking an input PDF document and running PDF Accessibility Auto-Tag API against it. Once the PDF has been tagged, we'll provide the document with tags and optionally, a report file. In this guide, we will walk you through the complete process for creating a program that will accomplish this task.`}</p>
    <h2 {...{
      "id": "prerequisites"
    }}>{`Prerequisites`}</h2>
    <p>{`To complete this guide, you will need:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://dotnet.microsoft.com/en-us/download"
        }}>{`.NET: version 8.0 or above`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://dotnet.microsoft.com/en-us/download/dotnet/8.0"
        }}>{`.Net SDK`}</a></li>
      <li parentName="ul">{`A build tool: Either Visual Studio or .NET Core CLI.`}</li>
      <li parentName="ul">{`An Adobe ID. If you do not have one, the credential setup will walk you through creating one.`}</li>
      <li parentName="ul">{`A way to edit code. No specific editor is required for this guide.`}</li>
    </ul>
    <h2 {...{
      "id": "step-one-getting-credentials"
    }}>{`Step One: Getting credentials`}</h2>
    <p>{`1) To begin, open your browser to `}<a parentName="p" {...{
        "href": "https://acrobatservices.adobe.com/dc-integration-creation-app-cdn/main.html?api=pdf-accessibility-auto-tag-api"
      }}>{`https://acrobatservices.adobe.com/dc-integration-creation-app-cdn/main.html?api=pdf-accessibility-auto-tag-api`}</a>{`. If you are not already logged in to Adobe.com, you will need to sign in or create a new user. Using a personal email account is recommend and not a federated ID.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "750px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "74.6875%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/5530d/shot1.webp 320w", "/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/0c8fb/shot1.webp 640w", "/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/3ea8a/shot1.webp 750w"],
            "sizes": "(max-width: 750px) 100vw, 750px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/dd4a7/shot1.png 320w", "/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/0f09e/shot1.png 640w", "/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/e9985/shot1.png 750w"],
            "sizes": "(max-width: 750px) 100vw, 750px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/e9985/shot1.png",
            "alt": "Sign in",
            "title": "Sign in",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`2) After registering or logging in, you will then be asked to name your new credentials. Use the name, "New Project". `}</p>
    <p>{`3) Change the "Choose language" setting to ".Net". `}</p>
    <p>{`4) Also note the checkbox by, "Create personalized code sample." This will include a large set of samples along with your credentials. These can be helpful for learning more later. `}</p>
    <p>{`5) Click the checkbox saying you agree to the developer terms and then click "Create credentials."`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1233px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "53.125%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/0d0044ab296dee06ecf3020847f364ed/5530d/shot2_spc.webp 320w", "/document-services/docs/static/0d0044ab296dee06ecf3020847f364ed/0c8fb/shot2_spc.webp 640w", "/document-services/docs/static/0d0044ab296dee06ecf3020847f364ed/e14cd/shot2_spc.webp 1233w"],
            "sizes": "(max-width: 1233px) 100vw, 1233px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/0d0044ab296dee06ecf3020847f364ed/dd4a7/shot2_spc.png 320w", "/document-services/docs/static/0d0044ab296dee06ecf3020847f364ed/0f09e/shot2_spc.png 640w", "/document-services/docs/static/0d0044ab296dee06ecf3020847f364ed/7644a/shot2_spc.png 1233w"],
            "sizes": "(max-width: 1233px) 100vw, 1233px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/0d0044ab296dee06ecf3020847f364ed/7644a/shot2_spc.png",
            "alt": "Project setup",
            "title": "Project setup",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`6) After your credentials are created, they are automatically  downloaded:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "42.8125%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/0604eb7f8f38efc5970b0310665692b9/5530d/shot3_spc.webp 320w", "/document-services/docs/static/0604eb7f8f38efc5970b0310665692b9/0c8fb/shot3_spc.webp 640w", "/document-services/docs/static/0604eb7f8f38efc5970b0310665692b9/94b1e/shot3_spc.webp 1280w", "/document-services/docs/static/0604eb7f8f38efc5970b0310665692b9/09047/shot3_spc.webp 1876w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/0604eb7f8f38efc5970b0310665692b9/dd4a7/shot3_spc.png 320w", "/document-services/docs/static/0604eb7f8f38efc5970b0310665692b9/0f09e/shot3_spc.png 640w", "/document-services/docs/static/0604eb7f8f38efc5970b0310665692b9/bbbf7/shot3_spc.png 1280w", "/document-services/docs/static/0604eb7f8f38efc5970b0310665692b9/bd320/shot3_spc.png 1876w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/0604eb7f8f38efc5970b0310665692b9/bbbf7/shot3_spc.png",
            "alt": "alt",
            "title": "alt",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <h2 {...{
      "id": "step-two-setting-up-the-project"
    }}>{`Step Two: Setting up the project`}</h2>
    <p>{`1) In your Downloads folder, find the ZIP file with your credentials: PDFServicesSDK-.NetSamples.zip. If you unzip that archive, you will find a folder of samples and the `}<inlineCode parentName="p">{`pdfservices-api-credentials.json`}</inlineCode>{` file.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "398px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "70%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/e0974e7539e47dfb8316f343863c30f1/5530d/shot5_spc.webp 320w", "/document-services/docs/static/e0974e7539e47dfb8316f343863c30f1/b8520/shot5_spc.webp 398w"],
            "sizes": "(max-width: 398px) 100vw, 398px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/e0974e7539e47dfb8316f343863c30f1/dd4a7/shot5_spc.png 320w", "/document-services/docs/static/e0974e7539e47dfb8316f343863c30f1/2ab25/shot5_spc.png 398w"],
            "sizes": "(max-width: 398px) 100vw, 398px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/e0974e7539e47dfb8316f343863c30f1/2ab25/shot5_spc.png",
            "alt": "alt",
            "title": "alt",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`2) Take the `}<inlineCode parentName="p">{`pdfservices-api-credentials.json`}</inlineCode>{` file and place it in a new directory.`}</p>
    <p>{`3) In your new directory, create a new file, `}<inlineCode parentName="p">{`AutotagPDF.csproj`}</inlineCode>{`. This file will declare our requirements as well as help define the application we're creating.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<Project Sdk="Microsoft.NET.Sdk">

    <PropertyGroup>
        <OutputType>Exe</OutputType>
        <TargetFramework>net8.0</TargetFramework>
    </PropertyGroup>

    <ItemGroup>
        <PackageReference Include="log4net" Version="2.0.17" />
        <PackageReference Include="Adobe.PDFServicesSDK" Version="4.2.0" />
    </ItemGroup>

    <ItemGroup>
        <None Update="Adobe Accesibility Auto-Tag API Sample.pdf">
            <CopyToOutputDirectory>Always</CopyToOutputDirectory>
        </None>
        <None Update="log4net.config">
            <CopyToOutputDirectory>Always</CopyToOutputDirectory>
        </None>
    </ItemGroup>

</Project>
`}</code></pre>
    <p>{`This file will define what dependencies we need and how the application will be built.`}</p>
    <p>{`Our application will take a PDF, `}<inlineCode parentName="p">{`Adobe Accesibility Auto-Tag API Sample.pdf`}</inlineCode>{` (downloadable from `}<a href="/document-services/docs/assets/b3b8ca5aad09665ff577cffb35ad98cf/Adobe_Accessibility_Auto_Tag_API_Sample.pdf" target="_blank">{`here`}</a>{`) and tag its contents. The results will be saved in a given directory `}<inlineCode parentName="p">{`/output`}</inlineCode>{`.`}</p>
    <p>{`4) In your editor, open the directory where you previously copied the credentials and created the `}<inlineCode parentName="p">{`csproj`}</inlineCode>{` file. Create a new file, `}<inlineCode parentName="p">{`Program.cs`}</inlineCode>{`. `}</p>
    <p>{`Now you're ready to begin coding.`}</p>
    <h2 {...{
      "id": "step-three-creating-the-application"
    }}>{`Step Three: Creating the application`}</h2>
    <p>{`1) We'll begin by including our required dependencies:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`using System;
using System.IO;
using System.Reflection;
using Adobe.PDFServicesSDK;
using Adobe.PDFServicesSDK.auth;
using Adobe.PDFServicesSDK.exception;
using Adobe.PDFServicesSDK.io;
using Adobe.PDFServicesSDK.pdfjobs.jobs;
using Adobe.PDFServicesSDK.pdfjobs.results;
using log4net;
using log4net.Config;
using log4net.Repository;
`}</code></pre>
    <p>{`2) Now let's define our main class and `}<inlineCode parentName="p">{`Main`}</inlineCode>{` method:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`namespace AutotagPDF
{
    class Program
    {
        private static readonly ILog log = LogManager.GetLogger(typeof(Program));
        static void Main()
        {
            
        }
    }
}
`}</code></pre>
    <p>{`3) Set the environment variables `}<inlineCode parentName="p">{`PDF_SERVICES_CLIENT_ID`}</inlineCode>{` and `}<inlineCode parentName="p">{`PDF_SERVICES_CLIENT_SECRET`}</inlineCode>{` by running the following commands and replacing placeholders `}<inlineCode parentName="p">{`YOUR CLIENT ID`}</inlineCode>{` and `}<inlineCode parentName="p">{`YOUR CLIENT SECRET`}</inlineCode>{` with the credentials present in `}<inlineCode parentName="p">{`pdfservices-api-credentials.json`}</inlineCode>{` file:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Windows:`}</strong></p>
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`set PDF_SERVICES_CLIENT_ID=<YOUR CLIENT ID>`}</inlineCode></li>
          <li parentName="ul"><inlineCode parentName="li">{`set PDF_SERVICES_CLIENT_SECRET=<YOUR CLIENT SECRET>`}</inlineCode></li>
        </ul>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`MacOS/Linux:`}</strong></p>
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`export PDF_SERVICES_CLIENT_ID=<YOUR CLIENT ID>`}</inlineCode></li>
          <li parentName="ul"><inlineCode parentName="li">{`export PDF_SERVICES_CLIENT_SECRET=<YOUR CLIENT SECRET>`}</inlineCode></li>
        </ul>
      </li>
    </ul>
    <p>{`4) Next, we can create our credentials and PDFServices instance:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// Initial setup, create credentials instance
ICredentials credentials = new ServicePrincipalCredentials(
        Environment.GetEnvironmentVariable("PDF_SERVICES_CLIENT_ID"),
        Environment.GetEnvironmentVariable("PDF_SERVICES_CLIENT_SECRET"));

// Creates a PDF Services instance
PDFServices pdfServices = new PDFServices(credentials);
`}</code></pre>
    <p>{`5) Now, let's upload the asset:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`IAsset asset = pdfServices.Upload(inputStream, PDFServicesMediaType.PDF.GetMIMETypeValue());
`}</code></pre>
    <p>{`We define input stream for the PDF that will be tagged. (You can download the source we used `}<a href="/document-services/docs/assets/b3b8ca5aad09665ff577cffb35ad98cf/Adobe_Accessibility_Auto_Tag_API_Sample.pdf" target="_blank">{`here`}</a>{`.) In a real application, these values would be typically be dynamic.
Then we upload the content of input stream and specify the input media type as PDF.`}</p>
    <p>{`6) Now, let's create the job and parameters:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// Create parameters for the job
AutotagPDFParams autotagPDFParams = AutotagPDFParams.AutotagPDFParamsBuilder().GenerateReport().Build();

// Creates a new job instance
AutotagPDFJob autotagPDFJob = new AutotagPDFJob(asset).SetParams(autotagPDFParams);
`}</code></pre>
    <p>{`This set of code defines what we're doing (an Auto-Tag operation),
it defines parameters for the Auto-Tag job. PDF Accessibility Auto-Tag API has a few different options, but in this example, we're simply asking for a basic tagging operation, which returns the tagged PDF document and an XLSX report of the document.`}</p>
    <p>{`7) The next code block submits the job and gets the job result:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// Submits the job and gets the job result
String location = pdfServices.Submit(autotagPDFJob);
PDFServicesResponse<AutotagPDFResult> pdfServicesResponse =
        pdfServices.GetJobResult<AutotagPDFResult>(location, typeof(AutotagPDFResult));

// Get content from the resulting asset(s)
IAsset resultAsset = pdfServicesResponse.Result.TaggedPDF;
IAsset resultAssetReport = pdfServicesResponse.Result.Report;
StreamAsset streamAsset = pdfServices.GetContent(resultAsset);
StreamAsset streamAssetReport = pdfServices.GetContent(resultAssetReport)
`}</code></pre>
    <p>{`8) The next code block saves the result at the specified location:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// Creating output streams and copying stream asset's content to it
Stream outputStream = File.OpenWrite(Directory.GetCurrentDirectory() + "output/autotag-tagged.pdf");
Stream outputStreamReport =
        File.OpenWrite(Directory.GetCurrentDirectory() + "output/autotag-report.xlsx");
streamAsset.Stream.CopyTo(outputStream);
streamAssetReport.Stream.CopyTo(outputStreamReport);
outputStream.Close();
outputStreamReport.Close();
`}</code></pre>
    <p>{`This code runs the Auto-Tagging process and then stores the result files in the provided output directory.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "935px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "23.4375%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/d81c772484eb93b51dff0601d61dd3a0/5530d/shot9_ga.webp 320w", "/document-services/docs/static/d81c772484eb93b51dff0601d61dd3a0/0c8fb/shot9_ga.webp 640w", "/document-services/docs/static/d81c772484eb93b51dff0601d61dd3a0/0597b/shot9_ga.webp 935w"],
            "sizes": "(max-width: 935px) 100vw, 935px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/d81c772484eb93b51dff0601d61dd3a0/dd4a7/shot9_ga.png 320w", "/document-services/docs/static/d81c772484eb93b51dff0601d61dd3a0/0f09e/shot9_ga.png 640w", "/document-services/docs/static/d81c772484eb93b51dff0601d61dd3a0/487e7/shot9_ga.png 935w"],
            "sizes": "(max-width: 935px) 100vw, 935px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/d81c772484eb93b51dff0601d61dd3a0/487e7/shot9_ga.png",
            "alt": "Example running in the command line",
            "title": "Example running in the command line",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`Here's the complete application (`}<inlineCode parentName="p">{`Program.cs`}</inlineCode>{`):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`using System;
using System.IO;
using System.Reflection;
using Adobe.PDFServicesSDK;
using Adobe.PDFServicesSDK.auth;
using Adobe.PDFServicesSDK.exception;
using Adobe.PDFServicesSDK.io;
using Adobe.PDFServicesSDK.pdfjobs.jobs;
using Adobe.PDFServicesSDK.pdfjobs.parameters.autotag;
using Adobe.PDFServicesSDK.pdfjobs.results;
using log4net;
using log4net.Config;
using log4net.Repository;

namespace AutotagPDF
{
    class Program
    {
        private static readonly ILog log = LogManager.GetLogger(typeof(Program));
        
        static void Main()
        {
            // Configure the logging
            ConfigureLogging();
            try
            {
                // Initial setup, create credentials instance
                ICredentials credentials = new ServicePrincipalCredentials(
                    Environment.GetEnvironmentVariable("PDF_SERVICES_CLIENT_ID"),
                    Environment.GetEnvironmentVariable("PDF_SERVICES_CLIENT_SECRET"));

                // Creates a PDF Services instance
                PDFServices pdfServices = new PDFServices(credentials);

                // Creates an asset(s) from source file(s) and upload
                using Stream inputStream = File.OpenRead(@"Adobe Accesibility Auto-Tag API Sample.pdf");
                IAsset asset = pdfServices.Upload(inputStream, PDFServicesMediaType.PDF.GetMIMETypeValue());

                // Create parameters for the job
                AutotagPDFParams autotagPDFParams = AutotagPDFParams.AutotagPDFParamsBuilder().GenerateReport().Build();

                // Creates a new job instance
                AutotagPDFJob autotagPDFJob = new AutotagPDFJob(asset).SetParams(autotagPDFParams);

                // Submits the job and gets the job result
                String location = pdfServices.Submit(autotagPDFJob);
                PDFServicesResponse<AutotagPDFResult> pdfServicesResponse =
                    pdfServices.GetJobResult<AutotagPDFResult>(location, typeof(AutotagPDFResult));

                // Get content from the resulting asset(s)
                IAsset resultAsset = pdfServicesResponse.Result.TaggedPDF;
                IAsset resultAssetReport = pdfServicesResponse.Result.Report;
                StreamAsset streamAsset = pdfServices.GetContent(resultAsset);
                StreamAsset streamAssetReport = pdfServices.GetContent(resultAssetReport);

                // Creating output streams and copying stream asset's content to it
                String outputFilePath = "/output/autotag-tagged.pdf";
                new FileInfo(Directory.GetCurrentDirectory() + outputFilePath).Directory.Create();
                Stream outputStream = File.OpenWrite(Directory.GetCurrentDirectory() + outputFilePath);

                String outputFilePathReport = "/output/autotag-report.xlsx";
                new FileInfo(Directory.GetCurrentDirectory() + outputFilePath).Directory.Create();
                Stream outputStreamReport =
                    File.OpenWrite(Directory.GetCurrentDirectory() + outputFilePathReport);
                streamAsset.Stream.CopyTo(outputStream);
                streamAssetReport.Stream.CopyTo(outputStreamReport);
                outputStream.Close();
                outputStreamReport.Close();
                
                Console.WriteLine("Saving asset at " + Directory.GetCurrentDirectory() + outputFilePath);
                Console.WriteLine("Saving asset at " + Directory.GetCurrentDirectory() + outputFilePathReport);
            }
            catch (ServiceUsageException ex)
            {
                log.Error("Exception encountered while executing operation", ex);
            }
            catch (ServiceApiException ex)
            {
                log.Error("Exception encountered while executing operation", ex);
            }
            catch (SDKException ex)
            {
                log.Error("Exception encountered while executing operation", ex);
            }
            catch (IOException ex)
            {
                log.Error("Exception encountered while executing operation", ex);
            }
            catch (Exception ex)
            {
                log.Error("Exception encountered while executing operation", ex);
            }
        }

        static void ConfigureLogging()
        {
            ILoggerRepository logRepository = LogManager.GetRepository(Assembly.GetEntryAssembly());
            XmlConfigurator.Configure(logRepository, new FileInfo("log4net.config"));
        }
    }
}
`}</code></pre>
    <h2 {...{
      "id": "next-steps"
    }}>{`Next Steps`}</h2>
    <p>{`Now that you've successfully performed your first operation, `}<a parentName="p" {...{
        "href": "https://developer.adobe.com/document-services/docs/overview/pdf-services-api/"
      }}>{`review the documentation`}</a>{` for many other examples and reach out on our `}<a parentName="p" {...{
        "href": "https://community.adobe.com/t5/document-services-apis/ct-p/ct-Document-Cloud-SDK"
      }}>{`forums`}</a>{` with any questions. Also remember the samples you downloaded while creating your credentials also have many demos.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      